import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, of } from 'rxjs';
import { map, mergeMap, reduce, switchMap } from 'rxjs/operators';
import { formatFromDate } from 'src/app/common/datetime_utility';
import { getPlatformDisplayName } from 'src/app/common/platformUtils';
import { beautifyCamelCaseString } from 'src/app/common/stringUtils';
import { EmailData } from 'src/app/components/actions/action-send-email/action-send-email.component';
import { ChangeEventJustificationDialogComponent } from 'src/app/components/note-dialog/change-event-justification-dialog.component';
import { IntegrationModel, Platform, ResourceType } from 'src/app/models/api';
import {
  Attachment,
  InventoryApplicationResourceProperties,
  InventoryAutomationResourceProperties,
  InventoryEntityFieldStatus,
  InventoryResourceModel,
  PowerPlatformApplicationQuarantineStatus,
  ResourceStatus,
} from 'src/app/models/api/inventoryResource.model';
import {
  ResetViolationsStatusApiModel,
  UpdatableViolationStatus,
  UpdateViolationsStatusApiModel,
  ViolationChangeJustificationApiModel,
  ViolationModel,
  ViolationsStatusUpdateResponse,
  ViolationStatus,
} from 'src/app/models/api/violations.model';
import { PermissionEnum } from 'src/app/models/auth0/permission.enum';
import { MenuAction } from 'src/app/models/menuAction.model';
import { IChangeEventJustificationComponentOutput } from 'src/app/models/popups/outputs/changeEventJustificationComponentOutput.interface';
import { LabelsProviderPipe } from 'src/app/pipes/labels-provider/labels-provider.pipe';
import { environment } from 'src/environments/environment';
import {
  ActionResponseModel,
  ActionsHttpServiceService,
  AutomationActivationActions,
  AutomationActivationParams,
  PpApplicationQuarantineStatus,
  RequiredParamsForActionsHttpRequests,
  RequiredParamsForSetAccessHttpRequests,
  RequiredParamsForViolationActionsHttpRequests,
  ResourceActionsResponseModel,
  SetApplicationQuarantineStateParamsModel,
  ViolationsActionsResponseModel,
} from '../../httpServices/actionsHttpService/actions-http-service.service';
import { ViolationsHttpService } from '../../httpServices/violationsHttpService/violations-http.service';
import { PermissionsService } from '../../infra/permissionsService/permissions.service';
import { StoreService } from '../../infra/store/store.service';
import { arePlatformActionsAllowed } from '../common/actionsHelpers';
import { CommonActionsService, EmailPlaceholder } from '../common/common-actions.service';
import { ViolationsSupportedActions } from './violationsSupportedActions';
import { QueryParams } from 'src/app/models/navigation/navigationParams.interface';
import { InventoryHttpService } from '../../httpServices/inventoryHttpService/inventory-http.service';
import { IViolationsViewModel } from 'src/app/models/viewModels/violationsViewModel';
import { JsonHalCollection } from 'src/app/models/api/jsonhal.model';
import { StatusCodeEnum } from 'src/app/models/api/integration.model';
import _ from 'lodash';

@Injectable({
  providedIn: 'root',
})
export class ViolationsActionsService {
  public readonly haveActions: boolean = true;
  private readonly _violationsEmailPlaceholders: EmailPlaceholder[];
  private _integrations: IntegrationModel[] = [];
  private readonly _actionIdResourceActionModelDictionary: {
    [actionId: string]: ViolationsSupportedActions;
  } = {
    powerplatformQuarantineCanvasApp: ViolationsSupportedActions.Quarantine,
    deletePlatformResource: ViolationsSupportedActions.Delete,
    listResourcePermissions: ViolationsSupportedActions.ListAccess,
    downloadResourceAttachment: ViolationsSupportedActions.GetResourceSbomFile,
    powerplatformStopFlow: ViolationsSupportedActions.Stop,
    getRawResource: ViolationsSupportedActions.GetRawResource,
    powerplatformSetCopilotAuthentication: ViolationsSupportedActions.PowerplatformSetCopilotAuthentication,
    powerplatformGetRawFlowRuns: ViolationsSupportedActions.PowerplatformGetRawFlowRuns,
    chatGptEnterpriseDeleteGptAction: ViolationsSupportedActions.ChatGptEnterpriseDeleteGptAction,
    chatGptEnterpriseIsolateGptAction: ViolationsSupportedActions.ChatGptEnterpriseIsolateGptAction,
    chatGptEnterpriseDeleteGptKnowledgeAction: ViolationsSupportedActions.ChatGptEnterpriseDeleteGptKnowledgeAction,
    chatGptEnterpriseGetGptKnowledgeFileUrlAction:
      ViolationsSupportedActions.ChatGptEnterpriseGetGptKnowledgeFileUrlAction,
    chatGptEnterpriseDeleteUserCanvasAction: ViolationsSupportedActions.ChatGptEnterpriseDeleteUserCanvasAction,
    chatGptEnterpriseGetUserCanvasAction: ViolationsSupportedActions.ChatGptEnterpriseGetUserCanvasAction,
  };
  private readonly _actionIdViolationActionModelDictionary: {
    [actionId: string]: ViolationsSupportedActions;
  } = {
    powerplatformSecureFlowSteps: ViolationsSupportedActions.SecureFlowSteps,
  }; // Currently there are only one action for violation, but we are keeping it as a dictionary for future use

  constructor(
    private readonly _violationsHttpService: ViolationsHttpService,
    private readonly _matDialog: MatDialog,
    private readonly _commonActionsService: CommonActionsService,
    private readonly _permissionsService: PermissionsService,
    private readonly _store: StoreService,
    private readonly _labelProvider: LabelsProviderPipe,
    private readonly _inventoryHttpService: InventoryHttpService,
    private readonly _httpActionsServiceService: ActionsHttpServiceService
  ) {
    this._store.integrations$.subscribe((integrations: IntegrationModel[]) => {
      this._integrations = integrations;
    });

    this._violationsEmailPlaceholders = [
      {
        label: this._labelProvider.transform('emailTemplatePlaceholders.violationName.label'),
        description: this._labelProvider.transform('emailTemplatePlaceholders.violationName.description'),
      },
      {
        label: this._labelProvider.transform('emailTemplatePlaceholders.violationId.label'),
        description: this._labelProvider.transform('emailTemplatePlaceholders.violationId.description'),
      },
      {
        label: this._labelProvider.transform('emailTemplatePlaceholders.violationSummary.label'),
        description: this._labelProvider.transform('emailTemplatePlaceholders.violationSummary.description'),
      },
      {
        label: this._labelProvider.transform('emailTemplatePlaceholders.violationSeverity.label'),
        description: this._labelProvider.transform('emailTemplatePlaceholders.violationSeverity.description'),
      },
      {
        label: this._labelProvider.transform('emailTemplatePlaceholders.risk.label'),
        description: this._labelProvider.transform('emailTemplatePlaceholders.risk.description'),
      },
      {
        label: this._labelProvider.transform('emailTemplatePlaceholders.triage.label'),
        description: this._labelProvider.transform('emailTemplatePlaceholders.triage.description'),
      },
      {
        label: this._labelProvider.transform('emailTemplatePlaceholders.violationFix.label'),
        description: this._labelProvider.transform('emailTemplatePlaceholders.violationFix.description'),
      },
      {
        label: this._labelProvider.transform('emailTemplatePlaceholders.violationFirstSeen.label'),
        description: this._labelProvider.transform('emailTemplatePlaceholders.violationFirstSeen.description'),
      },
      {
        label: this._labelProvider.transform('emailTemplatePlaceholders.resourceType.label'),
        description: this._labelProvider.transform('emailTemplatePlaceholders.resourceType.description'),
      },
      {
        label: this._labelProvider.transform('emailTemplatePlaceholders.linkToPlatform.label'),
        description: this._labelProvider.transform('emailTemplatePlaceholders.linkToPlatform.description'),
      },
      {
        label: this._labelProvider.transform('emailTemplatePlaceholders.linkToZenity.label'),
        description: this._labelProvider.transform('emailTemplatePlaceholders.linkToZenity.description'),
      },
      {
        label: this._labelProvider.transform('emailTemplatePlaceholders.resourceName.label'),
        description: this._labelProvider.transform('emailTemplatePlaceholders.resourceName.description'),
      },
    ];
  }

  public async setActionsForViolations(
    violations: ViolationModel[],
    violationsCurrentPage: IViolationsViewModel[]
  ): Promise<void> {
    const violationsByIntegrationId = _.groupBy(violations, 'integration.id');
    let flagCounterResources = 0;
    let flagCounterViolations = 0;
    const maxFlagCounter = 100;
    Object.keys(violationsByIntegrationId).forEach((integrationId) => {
      this._httpActionsServiceService
        .getActionsOfResources({
          integrationId: integrationId,
          resourcesIds: violationsByIntegrationId[integrationId].map((violation) => violation.resource.id),
        })
        .subscribe({
          next: (response) => {
            this._handleResourcesActionsResponse(integrationId, response, violationsCurrentPage);
            flagCounterResources = maxFlagCounter;
          },
        });
      this._httpActionsServiceService
        .getActionsOfViolations({
          integrationId: integrationId,
          violationsIds: violationsByIntegrationId[integrationId].map((violation) => violation.id),
        })
        .subscribe({
          next: (response) => {
            this._handleViolationsActionsResponse(integrationId, response, violationsCurrentPage);
            flagCounterViolations = maxFlagCounter;
          },
        });
    });
    while (flagCounterResources < maxFlagCounter || flagCounterViolations < maxFlagCounter) {
      flagCounterResources++;
      flagCounterViolations++;
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
  }

  public handleActionClicked(
    action: ViolationsSupportedActions,
    violation: ViolationModel
  ): Observable<ViolationsStatusUpdateResponse | boolean | undefined> {
    const resourceType = violation.resource.resourceType;
    const resourceName = violation.resource.displayName;
    const resourceUrl = violation.resource.platformInfo?.urls?.value?.webAppUrl?.value ?? 'N/A';
    if (action == ViolationsSupportedActions.Exempt) {
      return this._handleViolationStatusChangeActionClicked(violation, UpdatableViolationStatus.exempted);
    }
    if (action == ViolationsSupportedActions.Waive) {
      return this._handleViolationStatusResetActionClicked(violation);
    }
    if (action == ViolationsSupportedActions.SendEmail) {
      return this._commonActionsService.sendEmailAction(this._createEmailData(violation));
    }

    const actionsHttpParams = {
      integrationId: violation.integration.id,
      resourceId: violation.resource.id,
      resourceType: resourceType,
      resourceStatus: violation.resource.resourceState?.status,
    } as RequiredParamsForActionsHttpRequests;

    const actionOptionalParams = { platformUrl: resourceUrl };

    if (action == ViolationsSupportedActions.AddOwner) {
      return this._commonActionsService.addOrRemoveOwnerAction(actionsHttpParams, true, actionOptionalParams);
    }
    if (action == ViolationsSupportedActions.RemoveOwner) {
      return this._commonActionsService.addOrRemoveOwnerAction(actionsHttpParams, false, actionOptionalParams);
    }
    if (action == ViolationsSupportedActions.SetAccess) {
      const params = {
        ...actionsHttpParams,
        isChangeAccessActionEnabled: true,
      } as RequiredParamsForSetAccessHttpRequests;
      return this._commonActionsService.setAccessAction(params, actionOptionalParams);
    }
    if (action == ViolationsSupportedActions.ListAccess) {
      const params = {
        ...actionsHttpParams,
        isChangeAccessActionEnabled: false,
      } as RequiredParamsForSetAccessHttpRequests;
      return this._commonActionsService.setAccessAction(params, actionOptionalParams);
    }
    if (action == ViolationsSupportedActions.Delete) {
      return this._commonActionsService.deleteAction(resourceName, actionsHttpParams);
    }
    if (action === ViolationsSupportedActions.SecureFlowSteps) {
      const violation_actions_http_params = {
        violationId: violation.id,
        ...actionsHttpParams,
      } as RequiredParamsForViolationActionsHttpRequests;
      return this._commonActionsService.secureFlowSteps(violation_actions_http_params);
    }
    if (action == ViolationsSupportedActions.Quarantine || action == ViolationsSupportedActions.Unquarantine) {
      const params = {
        ...actionsHttpParams,
        quarantine_status:
          action == ViolationsSupportedActions.Quarantine
            ? PpApplicationQuarantineStatus.quarantined
            : PpApplicationQuarantineStatus.unquarantined,
      } as SetApplicationQuarantineStateParamsModel;
      return this._commonActionsService.setResourceQuarantineStateAction(resourceName, params);
    }
    if (action === ViolationsSupportedActions.Start || action == ViolationsSupportedActions.Stop) {
      if (resourceType === ResourceType.recipe || resourceType === ResourceType.flow) {
        const params = {
          ...actionsHttpParams,
          action:
            action === ViolationsSupportedActions.Start
              ? AutomationActivationActions.start
              : AutomationActivationActions.stop,
        } as AutomationActivationParams;
        return this._commonActionsService.automationActivation(params, resourceType);
      }
    }
    if (action == ViolationsSupportedActions.GetResourceDefinition) {
      return this._commonActionsService.showRawData(actionsHttpParams);
    }

    if (action == ViolationsSupportedActions.GetResourceSbomFile) {
      try {
        this._inventoryHttpService
          .getInventoryResource(violation.integration.id, violation.resource.id)
          .pipe(
            map((resource: InventoryResourceModel) => {
              const attachments = resource?.attachments ?? [];
              const sbomAttachment = this._commonActionsService.findSbomAttachment(attachments);
              if (!sbomAttachment) {
                // Since SBOM action shouldnt be available if there is no SBOM attachment, this should never happen
                throw new Error(
                  `SBOM attachment not found for action: '${action}'. ${attachments.length} attachments found`
                );
              }
              return sbomAttachment;
            }),
            switchMap((sbomAttachment: Attachment) =>
              this._commonActionsService.getResourceAttachment(actionsHttpParams, sbomAttachment.id)
            )
          )
          .subscribe();
      } catch (error) {
        console.error(`ViolationsActionsService handleActionClicked: ${action} failed.`, error);
        return of(false);
      }
      return of(true);
    }
    if (action == ViolationsSupportedActions.GetRawResource) {
      return this._commonActionsService.showRawData(actionsHttpParams);
    }
    if (action == ViolationsSupportedActions.PowerplatformSetCopilotAuthentication) {
      return this._commonActionsService.powerplatformSetCopilotAuthenticationAction(actionsHttpParams);
    }
    if (action == ViolationsSupportedActions.PowerplatformGetRawFlowRuns) {
      return this._commonActionsService.OpenShowFlowRunsConfirmationDialogDialog(actionsHttpParams);
    }
    if (action == ViolationsSupportedActions.ChatGptEnterpriseDeleteGptAction) {
      return this._commonActionsService.chatGptEnterpriseDeleteGptAction(resourceName, actionsHttpParams);
    }
    if (action == ViolationsSupportedActions.ChatGptEnterpriseIsolateGptAction) {
      return this._commonActionsService.chatGptEnterpriseIsolateGptAction(actionsHttpParams);
    }
    if (action == ViolationsSupportedActions.ChatGptEnterpriseGetGptKnowledgeFileUrlAction) {
      return this._commonActionsService.chatGptEnterpriseGetGptKnowledgeFileUrlAction(resourceName, actionsHttpParams);
    }
    if (action == ViolationsSupportedActions.ChatGptEnterpriseGetUserCanvasAction) {
      return this._commonActionsService.chatGptEnterpriseGetUserCanvasAction(resourceName, actionsHttpParams);
    }

    throw new Error(`ViolationsActionsService handleActionClicked: ${action} support not implemented.`);
  }

  public getViolationEmailPlaceholders(): EmailPlaceholder[] {
    return this._violationsEmailPlaceholders;
  }
  private _handleResourcesActionsResponse(
    integrationId: string,
    response: JsonHalCollection<ResourceActionsResponseModel>,
    violationsCurrentPage: IViolationsViewModel[]
  ): void {
    const resourcesActions = response._embedded.items;
    const resourcesIdsActionsDict: {
      [resourceId: string]: ResourceActionsResponseModel;
    } = {};
    resourcesActions.forEach((resourceActions) => {
      resourcesIdsActionsDict[resourceActions.resourceId] = resourceActions;
    });
    violationsCurrentPage.forEach((violation) => {
      if (!violation.menuActions) violation.menuActions = [];
      if (violation.integrationId == integrationId && violation.violation.resource.id in resourcesIdsActionsDict) {
        this._addMenuAction(
          violation.menuActions,
          this._getMenuActionForResource(violation, resourcesIdsActionsDict[violation.violation.resource.id].actions)
        );
      }
    });
  }

  private _handleViolationsActionsResponse(
    integrationId: string,
    response: JsonHalCollection<ViolationsActionsResponseModel>,
    violationsCurrentPage: IViolationsViewModel[]
  ): void {
    const violationsActions = response._embedded.items;
    const violationsIdsActionsDict: {
      [resourceId: string]: ViolationsActionsResponseModel;
    } = {};
    violationsActions.forEach((violationActions) => {
      violationsIdsActionsDict[violationActions.violationId] = violationActions;
    });
    violationsCurrentPage.forEach((violation) => {
      if (!violation.menuActions) violation.menuActions = [];
      if (violation.integrationId == integrationId && violation.violation.id in violationsIdsActionsDict) {
        this._addMenuAction(
          violation.menuActions,
          this._getMenuActionForViolation(violation, violationsIdsActionsDict[violation.violation.id].actions)
        );
      }
    });
  }

  private _getMenuActionForResource(violationView: IViolationsViewModel, actions: ActionResponseModel[]): MenuAction[] {
    const violation = violationView.violation;
    const resourceType = violation.resource.resourceType;
    const resourceStatus = violation.resource.resourceState?.status;
    const newActions: MenuAction[] = [];
    if (violation.status !== ViolationStatus.resolved) {
      if (violation.status != ViolationStatus.exempted) {
        newActions.push(this._getMenuAction(ViolationsSupportedActions.Exempt, resourceType));
      }
      if (violation.status != ViolationStatus.open) {
        newActions.push(this._getMenuAction(ViolationsSupportedActions.Waive, resourceType));
      }
      actions.forEach((action) => {
        let flag = true;
        const actionModel = this._actionIdResourceActionModelDictionary[action.id];
        if (actionModel) {
          if (actionModel == ViolationsSupportedActions.Quarantine) {
            const application_properties = violation.resource
              ?.resourceCategoryProperties as InventoryApplicationResourceProperties;
            if (
              application_properties !== undefined &&
              application_properties.quarantineStatus.status === InventoryEntityFieldStatus.hasValue
            ) {
              const newAction =
                application_properties.quarantineStatus.value ===
                PowerPlatformApplicationQuarantineStatus.Unquarantined.toString()
                  ? ViolationsSupportedActions.Quarantine
                  : ViolationsSupportedActions.Unquarantine;
              newActions.push(this._getMenuAction(newAction, resourceType, resourceStatus));
            }
            flag = false;
          }
          if (actionModel == ViolationsSupportedActions.Stop) {
            const flowProperties = violation.resource
              ?.resourceCategoryProperties as InventoryAutomationResourceProperties;
            // do not allow start/stop on suspended flow
            if (
              flowProperties.automationStatus.status == InventoryEntityFieldStatus.hasValue &&
              flowProperties.automationStatus.value !== 'Suspended'
            ) {
              const status = resourceStatus;
              const newAction =
                status == ResourceStatus.active ? ViolationsSupportedActions.Stop : ViolationsSupportedActions.Start;
              newActions.push(this._getMenuAction(newAction, resourceType, resourceStatus));
            }
            flag = false;
          }
          if (actionModel == ViolationsSupportedActions.ListAccess) {
            const removeAccess = 'removeResourcePermissions';
            const actionsIds = actions.map((action) => action.id);
            if (actionsIds.includes(removeAccess)) {
              newActions.push(this._getMenuAction(ViolationsSupportedActions.SetAccess, resourceType, resourceStatus));
            } else {
              newActions.push(this._getMenuAction(ViolationsSupportedActions.ListAccess, resourceType, resourceStatus));
            }
            flag = false;
          }
          if (flag)
            newActions.push(
              this._getMenuAction(this._actionIdResourceActionModelDictionary[action.id], resourceType, resourceStatus)
            );
        }
      });
      // Our new API doesn't support all Powerplatform platform actions yet. Thus, we are taking the old code.
      if (violation.resource.platformInfo.platform == Platform.powerplatform) {
        if (this._commonActionsService.supportedAddOwnersResourceTypesArray.includes(resourceType)) {
          newActions.push(this._getMenuAction(ViolationsSupportedActions.AddOwner, resourceType, resourceStatus));
        }
        if (this._commonActionsService.supportedRemoveOwnersResourceTypesArray.includes(resourceType)) {
          newActions.push(this._getMenuAction(ViolationsSupportedActions.RemoveOwner, resourceType, resourceStatus));
        }
        if (this._commonActionsService.isGetDefinitionOptionVisible(Platform.powerplatform, resourceType)) {
          const integrationId = violation?.integration.id;
          if (integrationId) {
            this._commonActionsService.isGetDefinitionOptionEnabled(integrationId).subscribe((value) => {
              const disabledReason = value?.disabledReason;
              const action = {
                name: ViolationsSupportedActions.GetResourceDefinition.toString(),
                displayNameLabel:
                  resourceType == ResourceType.flow
                    ? 'commonActions.getFlowDefinition'
                    : 'commonActions.getResourceDefinition',
                disabled: disabledReason != undefined,
                tooltip: disabledReason,
              };
              newActions.push(action);
            });
          }
        }
      }
      // Our new API doesn't support Workato platform actions yet. Thus, we are taking the old code.
      if (violation.resource.platformInfo.platform == Platform.workato) {
        const integration = this._integrations.filter((integration) => integration.id == violation.integration.id);
        if (
          integration.length == 1 &&
          arePlatformActionsAllowed(Platform.workato, integration[0].authenticationMethod)
        ) {
          if (resourceType === ResourceType.recipe) {
            const action =
              resourceStatus === ResourceStatus.active
                ? ViolationsSupportedActions.Stop
                : ViolationsSupportedActions.Start;
            newActions.push(this._getMenuAction(action, resourceType, resourceStatus));

            newActions.push(this._getMenuAction(ViolationsSupportedActions.Delete, resourceType, resourceStatus));
          }
        }
      }
    }
    newActions.push(this._getMenuAction(ViolationsSupportedActions.SendEmail, resourceType));
    return newActions;
  }

  private _getMenuActionForViolation(
    violationView: IViolationsViewModel,
    actions: ActionResponseModel[]
  ): MenuAction[] {
    const violation = violationView.violation;
    const resourceType = violation.resource.resourceType;
    const resourceStatus = violation.resource.resourceState?.status;
    const newActions: MenuAction[] = [];
    if (violation.status !== ViolationStatus.resolved) {
      actions.forEach((action) => {
        const actionModel = this._actionIdViolationActionModelDictionary[action.id];
        if (actionModel && actionModel == ViolationsSupportedActions.SecureFlowSteps) {
          const integration = this._integrations.find((integration) => integration.id == violation.integration.id);
          if (integration?.connectionStatus.code == StatusCodeEnum.on) {
            const newAction = this._getMenuAction(
              ViolationsSupportedActions.SecureFlowSteps,
              resourceType,
              resourceStatus
            );
            newActions.push(newAction);
          }
        }
      });
    }
    return newActions;
  }

  private _addMenuAction(menuActions: MenuAction[], menuActions2: MenuAction[]): void {
    const actionsNamesToAddInTheEnd: string[] = [ViolationsSupportedActions.SecureFlowSteps.toString()];
    const actionsToAddInTheEnd: MenuAction[] = [];
    menuActions.forEach((action) => {
      if (actionsNamesToAddInTheEnd.includes(action.name)) {
        actionsToAddInTheEnd.push(action);
        menuActions.splice(menuActions.indexOf(action), 1);
      }
    });

    menuActions2.forEach((action) => {
      if (!menuActions.find((menuAction) => menuAction.name == action.name)) {
        menuActions.push(action);
      }
    });
    actionsToAddInTheEnd.forEach((action) => {
      menuActions.push(action);
    });
  }

  private _handleViolationStatusChangeActionClicked(
    violation: ViolationModel,
    newStatus: UpdatableViolationStatus
  ): Observable<ViolationsStatusUpdateResponse | undefined> {
    return this._openChangeEventJustificationDialog().pipe(
      mergeMap((output: IChangeEventJustificationComponentOutput) => {
        if (output.addJustification && output.content) {
          const updateModel = {
            ids: [violation.id],
            status: newStatus,
            change_reason: {
              justification: output.content,
            } as ViolationChangeJustificationApiModel,
          } as UpdateViolationsStatusApiModel;
          return this._violationsHttpService
            .updateViolationStatus(violation.integration.id, updateModel)
            .pipe(reduce((data) => data));
        }
        return of(undefined);
      })
    );
  }

  private _handleViolationStatusResetActionClicked(
    violation: ViolationModel
  ): Observable<ViolationsStatusUpdateResponse | undefined> {
    return this._openChangeEventJustificationDialog().pipe(
      mergeMap((output: IChangeEventJustificationComponentOutput) => {
        if (output.addJustification && output.content) {
          const resetModel = {
            ids: [violation.id],
            reset_reason: {
              justification: output.content,
            } as ViolationChangeJustificationApiModel,
          } as ResetViolationsStatusApiModel;
          return this._violationsHttpService.resetViolationStatus(violation.integration.id, resetModel);
        }
        return of(undefined);
      })
    );
  }

  private _getMenuAction(
    supportedAction: ViolationsSupportedActions,
    resourceType: ResourceType,
    inventoryResourceStatus?: ResourceStatus
  ): MenuAction {
    let hasPermissions;
    const isDeleted = this._isResourceDeleted(inventoryResourceStatus);
    switch (supportedAction) {
      case ViolationsSupportedActions.Exempt:
        hasPermissions = this._permissionsService.hasPermissions(PermissionEnum.operator);
        return {
          name: ViolationsSupportedActions.Exempt.toString(),
          displayNameLabel: 'violationActions.exempt',
          disabled: !hasPermissions,
          tooltip: !hasPermissions ? this._labelProvider.transform('commonActions.adminActionTooltip') : '',
        } as MenuAction;
      case ViolationsSupportedActions.Waive:
        hasPermissions = this._permissionsService.hasPermissions(PermissionEnum.operator);
        return {
          name: ViolationsSupportedActions.Waive.toString(),
          displayNameLabel: 'violationActions.waive',
          disabled: !hasPermissions,
          tooltip: !hasPermissions ? this._labelProvider.transform('commonActions.adminActionTooltip') : '',
        } as MenuAction;
      case ViolationsSupportedActions.AddOwner:
        hasPermissions = this._permissionsService.hasPermissions(
          this._commonActionsService.getPermissionRequiredForResourceAction(resourceType)
        );
        return {
          name: ViolationsSupportedActions.AddOwner.toString(),
          displayNameLabel: 'commonActions.addOwner',
          disabled: !hasPermissions || isDeleted,
          tooltip: !hasPermissions
            ? this._labelProvider.transform('commonActions.adminActionTooltip')
            : isDeleted
              ? this._labelProvider.transform('commonActions.deletedResourceTooltip')
              : '',
        } as MenuAction;
      case ViolationsSupportedActions.RemoveOwner:
        hasPermissions = this._permissionsService.hasPermissions(
          this._commonActionsService.getPermissionRequiredForResourceAction(resourceType)
        );
        return {
          name: ViolationsSupportedActions.RemoveOwner.toString(),
          displayNameLabel: 'commonActions.removeOwner',
          disabled: !hasPermissions || isDeleted,
          tooltip: !hasPermissions
            ? this._labelProvider.transform('commonActions.adminActionTooltip')
            : isDeleted
              ? this._labelProvider.transform('commonActions.deletedResourceTooltip')
              : '',
        } as MenuAction;
      case ViolationsSupportedActions.ListAccess:
        return {
          name: ViolationsSupportedActions.ListAccess.toString(),
          displayNameLabel: 'commonActions.setAccess',
          disabled: isDeleted,
        } as MenuAction;
      case ViolationsSupportedActions.SetAccess:
        return {
          name: ViolationsSupportedActions.SetAccess.toString(),
          displayNameLabel: 'commonActions.setAccess',
          disabled: isDeleted,
        };
      case ViolationsSupportedActions.Delete:
        hasPermissions = this._permissionsService.hasPermissions(PermissionEnum.admin);
        return {
          name: ViolationsSupportedActions.Delete.toString(),
          displayNameLabel: 'commonActions.delete',
          disabled: !hasPermissions || isDeleted,
          tooltip: !hasPermissions
            ? this._labelProvider.transform('commonActions.adminActionTooltip')
            : isDeleted
              ? this._labelProvider.transform('commonActions.deletedResourceTooltip')
              : '',
        } as MenuAction;
      case ViolationsSupportedActions.SendEmail:
        hasPermissions = this._permissionsService.hasPermissions(PermissionEnum.operator);
        return {
          name: ViolationsSupportedActions.SendEmail.toString(),
          displayNameLabel: 'commonActions.sendEmail',
          disabled: !hasPermissions,
          tooltip: !hasPermissions ? this._labelProvider.transform('commonActions.adminActionTooltip') : '',
        } as MenuAction;
      case ViolationsSupportedActions.Quarantine:
        hasPermissions = this._permissionsService.hasPermissions(PermissionEnum.operator);
        return {
          name: ViolationsSupportedActions.Quarantine.toString(),
          displayNameLabel: 'commonActions.quarantine',
          disabled: !hasPermissions,
          tooltip: !hasPermissions ? this._labelProvider.transform('commonActions.adminActionTooltip') : '',
        } as MenuAction;
      case ViolationsSupportedActions.Unquarantine:
        hasPermissions = this._permissionsService.hasPermissions(
          this._commonActionsService.getPermissionRequiredForResourceAction(resourceType)
        );
        return {
          name: ViolationsSupportedActions.Unquarantine.toString(),
          displayNameLabel: 'commonActions.unquarantine',
          disabled: !hasPermissions || isDeleted,
          tooltip: !hasPermissions
            ? this._labelProvider.transform('commonActions.adminActionTooltip')
            : isDeleted
              ? this._labelProvider.transform('commonActions.deletedResourceTooltip')
              : '',
        } as MenuAction;
      case ViolationsSupportedActions.Start:
        hasPermissions = this._permissionsService.hasPermissions(PermissionEnum.operator);
        return {
          name: ViolationsSupportedActions.Start.toString(),
          displayNameLabel: 'commonActions.start',
          disabled: !hasPermissions,
          tooltip: !hasPermissions ? this._labelProvider.transform('commonActions.operatorActionTooltip') : '',
        } as MenuAction;
      case ViolationsSupportedActions.Stop:
        hasPermissions = this._permissionsService.hasPermissions(PermissionEnum.operator);
        return {
          name: ViolationsSupportedActions.Stop.toString(),
          displayNameLabel: 'commonActions.stop',
          disabled: !hasPermissions,
          tooltip: !hasPermissions ? this._labelProvider.transform('commonActions.operatorActionTooltip') : '',
        } as MenuAction;
      case ViolationsSupportedActions.SecureFlowSteps:
        hasPermissions = this._permissionsService.hasPermissions(PermissionEnum.operator);
        return {
          name: ViolationsSupportedActions.SecureFlowSteps.toString(),
          displayNameLabel: 'commonActions.secureFlowSteps',
          disabled: !hasPermissions,
          tooltip: !hasPermissions ? this._labelProvider.transform('commonActions.operatorActionTooltip') : '',
        } as MenuAction;
      case ViolationsSupportedActions.GetResourceSbomFile:
        return {
          name: ViolationsSupportedActions.GetResourceSbomFile.toString(),
          displayNameLabel: 'commonActions.getResourceSbomFile',
          disabled: isDeleted,
        } as MenuAction;
      case ViolationsSupportedActions.GetRawResource:
        return {
          name: ViolationsSupportedActions.GetRawResource.toString(),
          displayNameLabel: 'commonActions.getRawResourceAction.actionName',
          disabled: isDeleted,
        } as MenuAction;
      case ViolationsSupportedActions.PowerplatformSetCopilotAuthentication:
        return {
          name: ViolationsSupportedActions.PowerplatformSetCopilotAuthentication.toString(),
          displayNameLabel: 'commonActions.powerplatformSetCopilotAuthentication.actionName',
          disabled: isDeleted,
        } as MenuAction;
      case ViolationsSupportedActions.PowerplatformGetRawFlowRuns:
        return {
          name: ViolationsSupportedActions.PowerplatformGetRawFlowRuns.toString(),
          displayNameLabel: 'commonActions.getFlowRunsAction.displayName',
          disabled: isDeleted,
        } as MenuAction;
      case ViolationsSupportedActions.ChatGptEnterpriseDeleteGptAction:
        return {
          name: ViolationsSupportedActions.ChatGptEnterpriseDeleteGptAction.toString(),
          displayNameLabel: 'commonActions.delete',
          disabled: isDeleted,
        } as MenuAction;
      case ViolationsSupportedActions.ChatGptEnterpriseIsolateGptAction:
        return {
          name: ViolationsSupportedActions.ChatGptEnterpriseIsolateGptAction.toString(),
          displayNameLabel: 'commonActions.isolate',
          disabled: isDeleted,
        } as MenuAction;
      case ViolationsSupportedActions.ChatGptEnterpriseDeleteGptKnowledgeAction:
        return {
          name: ViolationsSupportedActions.ChatGptEnterpriseDeleteGptKnowledgeAction.toString(),
          displayNameLabel: 'commonActions.delete',
          disabled: isDeleted,
        } as MenuAction;
      case ViolationsSupportedActions.ChatGptEnterpriseGetGptKnowledgeFileUrlAction:
        return {
          name: ViolationsSupportedActions.ChatGptEnterpriseGetGptKnowledgeFileUrlAction.toString(),
          displayNameLabel: 'commonActions.getGptKnowledge.title',
          disabled: isDeleted,
        } as MenuAction;
      case ViolationsSupportedActions.ChatGptEnterpriseDeleteUserCanvasAction:
        return {
          name: ViolationsSupportedActions.ChatGptEnterpriseDeleteUserCanvasAction.toString(),
          displayNameLabel: 'commonActions.delete',
          disabled: isDeleted,
        } as MenuAction;
      case ViolationsSupportedActions.ChatGptEnterpriseGetUserCanvasAction:
        return {
          name: ViolationsSupportedActions.ChatGptEnterpriseDeleteUserCanvasAction.toString(),
          displayNameLabel: 'commonActions.delete',
          disabled: isDeleted,
        } as MenuAction;
      default:
        throw new Error(`ViolationsActionsService getMenuAction: The action ${supportedAction} is not supported`);
    }
  }

  private _isResourceDeleted(inventoryResourceStatus?: ResourceStatus): boolean {
    return (
      inventoryResourceStatus != undefined &&
      inventoryResourceStatus != null &&
      inventoryResourceStatus === ResourceStatus.pendingDeletion
    );
  }

  private _createEmailData(violation: ViolationModel): EmailData {
    const resourceMakerEmails = this._commonActionsService.getResourceMakerEmails(
      violation.resource.identities?.ownedBy,
      violation.resource.identities?.createdBy
    );
    const platformDisplayName = getPlatformDisplayName(violation.resource.platformInfo.platform);
    const platformTypeDisplayName = beautifyCamelCaseString(violation.resource.resourceType);
    const platformLink = violation.resource.platformInfo?.urls.value?.webAppUrl?.value ?? 'N/A';

    let fixSuggestionText = '';
    if (violation.rule.riskReason) {
      fixSuggestionText += `Why is this a risk?
${violation.rule.riskReason
  .replace(/\r?\t/, '')
  .split('\n')
  .map((str) => str.trim())
  .join('\n')}\n\n`;
    }
    fixSuggestionText += `How to triage?
${violation.rule.triageSuggestion
  .replace(/\r?\t/, '')
  .split('\n')
  .map((str) => '* ' + str.trim())
  .join('\n')}`;
    if (violation.rule.fixSuggestion) {
      fixSuggestionText += `

How to fix?
${violation.rule.fixSuggestion
  .replace(/\r?\t/, '')
  .split('\n')
  .map((str) => str.trim())
  .join('\n')}`;
    }

    const to = '';
    const subject = `Violation found - ${violation.rule.displayName}`;
    const body = `Hi,

This is an automated email notifying you about a security violation recently found in ${platformDisplayName}.

* The Violation Impacts "${violation.resource.displayName}" which is a ${platformTypeDisplayName} in ${platformDisplayName}.
* The security risk has been rated as ${violation.rule.severity} risk and was first seen at ${formatFromDate(violation.firstSeen)}.

${fixSuggestionText}

Helpful links:
* Violation on Zenity: ${environment.app_base_url}/app/violations?${QueryParams.integration}=${violation.integration.id}&${QueryParams.violation}=${violation.id}

* ${platformDisplayName} ${platformTypeDisplayName} resource: ${platformLink}

Please act swiftly to mitigate the violation found and remove the risk.
For more information or questions please reach out to us at support@zenity.io`;

    return {
      to: to,
      subject: subject,
      body: body,
      integrationId: violation.integration.id,
      violationId: violation.id,
      resourceId: violation.resource.id,
      resourceMakerEmails: resourceMakerEmails,
      emailPlaceholders: this.getViolationEmailPlaceholders(),
    } as EmailData;
  }

  private _openChangeEventJustificationDialog(): Observable<IChangeEventJustificationComponentOutput> {
    const dialogRef = this._matDialog.open(ChangeEventJustificationDialogComponent, {
      disableClose: false,
      panelClass: 'sized-popup-dialog-container',
    });

    return dialogRef.afterClosed().pipe(map((output) => output || { addJustification: false }));
  }
}
